import { sumBy as _sumBy, times as _times, uniq as _uniq } from 'lodash-es'
import { Factory, Model } from 'miragejs'
import type { MockServer } from '@@/api/mock-server/server.js'
import {
  PROVET_PAY_PAYMENTS_ENDPOINTS,
  PROVET_PAY_PAYMENTS_GROUPABLE_COLUMNS,
  PROVET_PAY_PAYMENTS_RESOURCE_TYPES,
} from '@/constants/reports/provetPayPaymentReport.js'
import { filterable, pageable, sortable } from '../utils.js'
import {
  rand,
  randAmount,
  randBetweenDate,
  randBoolean,
  randNumber,
} from '@ngneat/falso'
import { addToDate, subtractToDate } from '@/utils/date/calculation.js'
import {
  PROVET_DATEPICKER_DATE_FORMAT,
  formatDate,
  formatToDateTimeISO,
  formatToJSDate,
} from '@/utils/date/conversion.js'
import { getTodayDateTimeISO, getTodayJSDate } from '@/utils/date/getter.js'
import { isAfter, isBefore } from '@/utils/date/validation.js'
import { toNumber } from '@/utils/number.js'
import { calculateProvetPayFees } from '@/utils/payments.js'
import type {
  ProvetPayPaymentFilters,
  ProvetPayPaymentGroupItemAttributes,
  ProvetPayPaymentListItemAttributes,
} from '@/models/reports/provetPayPaymentReport.js'

const MOCK_KEY = 'provetPayPaymentReport'

// Fixed pool of client names for duplications (15 names = ~13 items per client with 200 records)
const CLIENT_NAMES_POOL = [
  'John Smith',
  'Emma Johnson',
  'Michael Williams',
  'Sarah Brown',
  'David Jones',
  'Lisa Garcia',
  'James Miller',
  'Jennifer Davis',
  'Robert Martinez',
  'Maria Rodriguez',
  'William Anderson',
  'Elizabeth Taylor',
  'Thomas Wilson',
  'Patricia Moore',
  'Christopher Jackson',
]

const _calculateProvetPayNet = (
  attributes: ProvetPayPaymentListItemAttributes,
) =>
  toNumber(attributes.payment_amount) -
  calculateProvetPayFees(attributes) -
  Math.abs(toNumber(attributes.reversal_amount))

// Pool of recent payment dates (last ~60 days), computed at module load so mocks stay current.
const PAYMENT_DATES_POOL: string[] = _times(15, (i) =>
  formatDate(
    subtractToDate(getTodayJSDate(), { days: i * 4 }),
    PROVET_DATEPICKER_DATE_FORMAT,
  ),
)

export const model = Model.extend({})

const CURRENCY_POOL = ['EUR', 'USD', 'GBP', 'SEK', 'NOK', 'DKK', null]
const MOCK_CURRENCY = rand(CURRENCY_POOL)

export const factory = Factory.extend<ProvetPayPaymentListItemAttributes>({
  account_description() {
    return rand(['Main Account', 'Secondary Account'])
  },
  channel() {
    return rand(['IN_PERSON', 'ONLINE', null])
  },
  client() {
    return rand(CLIENT_NAMES_POOL)
  },
  client_id() {
    return randNumber({ max: 9999, min: 1 })
  },
  clinic_location() {
    return rand(['Main Clinic', 'Downtown Clinic', 'Branch A', null])
  },
  currency() {
    return MOCK_CURRENCY
  },
  invoice_id() {
    return randNumber({ max: 99999, min: 10000 })
  },
  nh_markup() {
    return +randAmount({ max: 2, min: 0.1 })
  },
  nh_processing_fee() {
    return +randAmount({ max: 3, min: 0.1 })
  },
  nordhealth_fee() {
    return +randAmount({ max: 4, min: 0.2 })
  },
  other_fees() {
    return +randAmount({ max: 2, min: 0.1 })
  },
  paid_out() {
    if (this.result !== 'Success') {
      return 'No'
    }
    return randBoolean() ? 'Yes' : 'No'
  },
  payment_amount() {
    return +randAmount({ max: 500, min: 10 })
  },
  payment_date_ts_utc() {
    const date = rand(PAYMENT_DATES_POOL)
    // Anchor at local noon so the displayed local date always matches the
    // picked date regardless of viewer's timezone.
    return formatToDateTimeISO(addToDate(date, { hours: 12 })) ?? ''
  },
  payment_method() {
    return rand([
      'visa',
      'mc',
      'mastercard',
      'apple_pay',
      'applepay',
      'amex',
      'paypal',
      'klarna',
      'google_pay',
      'googlepay',
      'maestro',
      'mc apple_pay',
      'amex apple_pay',
      'visa google_pay',
      'mc google_pay',
      'visa applepay',
      'amex googlepay',
      'mc applepay',
      'mc_applepay',
      'visa_applepay',
      'amex_googlepay',
      'mc_googlepay',
    ])
  },
  payment_method_summary() {
    return rand([String(randNumber({ max: 9999, min: 1000 })), null])
  },
  payment_source() {
    return rand(['IN_PERSON', 'CARD_ON_FILE', 'PAYMENT_LINK', null])
  },
  payment_status() {
    const hasReversal = randBoolean()
    if (!hasReversal) {
      return 'Payment'
    }
    return rand(['Refund', 'Dispute', 'Chargeback', 'ChargebackReversed'])
  },
  payout_amount() {
    return _calculateProvetPayNet(this as ProvetPayPaymentListItemAttributes)
  },
  payout_date_ts_utc() {
    if (this.paid_out !== 'Yes') {
      return null
    }
    return randBetweenDate({
      from: subtractToDate(getTodayJSDate(), { days: 60 }),
      to: formatToJSDate(getTodayDateTimeISO()),
    })
      .toISOString()
      .split('T')[0]
  },
  refusal_reason() {
    if (this.result === 'Fail') {
      return rand([
        'Refused',
        'Card declined',
        'Insufficient funds',
        'Expired card',
      ])
    }
    return null
  },
  related_invoice() {
    return `INV-${randNumber({ max: 99999, min: 10000 })}`
  },
  result() {
    return rand(['Success', 'Fail'])
  },
  reversal_amount() {
    const hasReversal = this.payment_status !== 'Payment'
    return hasReversal ? Math.abs(+randAmount({ max: 300, min: 10 })) : null
  },
  reversal_fee() {
    const hasReversal = this.payment_status !== 'Payment'
    return hasReversal ? Math.abs(+randAmount({ max: 50, min: 5 })) : null
  },
  terminal() {
    return `TERM-${randNumber({ max: 9999999999, min: 1000000000 })}`
  },
  transaction_id() {
    return `TXN-${randNumber({ max: 999999999, min: 100000000 })}`
  },
})

export function seeds(server: MockServer) {
  server.createList(MOCK_KEY, 200)
}

/**
 * Helper to check if an item's value is included in a comma-separated filter string.
 * Case-insensitive comparison.
 */
const includesInCommaSeparated = (
  itemValue: string | null | undefined,
  filterValue: string,
) =>
  filterValue
    .split(',')
    .some(
      (v) =>
        v.localeCompare(itemValue ?? '', undefined, { sensitivity: 'base' }) ===
        0,
    )

export function routes(server: MockServer) {
  server.get(PROVET_PAY_PAYMENTS_ENDPOINTS.LIST, function (schema, request) {
    const results = schema.all(MOCK_KEY)

    const sorted = sortable(results, request, {
      account_description: (item) => item.attrs.account_description,
      nordhealth_fee: (item) => item.attrs.nordhealth_fee,
      paid_out: (item) => item.attrs.paid_out,
      payment_amount: (item) => item.attrs.payment_amount,
      payment_date_ts_utc: (item) => item.attrs.payment_date_ts_utc,
      payment_method: (item) => item.attrs.payment_method,
      payment_status: (item) => item.attrs.payment_status,
      payout_date_ts_utc: (item) => item.attrs.payout_date_ts_utc,
      result: (item) => item.attrs.result,
      reversal_amount: (item) => item.attrs.reversal_amount,
    })

    const filtered = filterable(sorted, request, {
      'account_description.in': (item, value) =>
        includesInCommaSeparated(item.attrs.account_description, value),
      'channel.in': (item, value) =>
        includesInCommaSeparated(item.attrs.channel, value),
      'clinic_location.in': (item, value) =>
        includesInCommaSeparated(item.attrs.clinic_location, value),
      'paid_out.in': (item, value) =>
        includesInCommaSeparated(item.attrs.paid_out, value),
      'payment_date_ts_utc.gte': (item, value) =>
        !isBefore(item.attrs.payment_date_ts_utc, value),
      'payment_date_ts_utc.lte': (item, value) =>
        !isAfter(item.attrs.payment_date_ts_utc, value),
      'payment_method.in': (item, value) =>
        includesInCommaSeparated(item.attrs.payment_method, value),
      'payment_source.in': (item, value) =>
        includesInCommaSeparated(item.attrs.payment_source, value),
      'payment_status.in': (item, value) =>
        includesInCommaSeparated(item.attrs.payment_status, value),
      'payout_date_ts_utc.gte': (item, value) =>
        !!item.attrs.payout_date_ts_utc &&
        !isBefore(item.attrs.payout_date_ts_utc, value),
      'payout_date_ts_utc.lte': (item, value) =>
        !!item.attrs.payout_date_ts_utc &&
        !isAfter(item.attrs.payout_date_ts_utc, value),
      'result.in': (item, value) =>
        includesInCommaSeparated(item.attrs.result, value),
      search: (item, value) => {
        const searchLower = value.toLowerCase()
        return (item.attrs.related_invoice ?? '')
          .toLowerCase()
          .includes(searchLower)
      },
    })

    // Compute aggregation counts from filtered results
    const allFilteredModels = filtered.models
    const paymentCount = allFilteredModels.filter(
      (i) => i.attrs.payment_status === 'Payment',
    ).length
    const refundCount = allFilteredModels.filter(
      (i) => i.attrs.payment_status === 'Refund',
    ).length
    const disputeCount = allFilteredModels.filter((i) =>
      ['Dispute', 'Chargeback', 'ChargebackReversed'].includes(
        i.attrs.payment_status,
      ),
    ).length

    const { data, meta } = pageable(filtered, request)

    return {
      data: data.models.map((item) => ({
        id: item.id,
        attributes: {
          ...item.attrs,
        },
        type: PROVET_PAY_PAYMENTS_RESOURCE_TYPES.PAYMENT,
      })),
      meta: {
        aggregation: {
          dispute_count: disputeCount,
          payment_count: paymentCount,
          refund_count: refundCount,
        },
        pagination: meta,
      },
    }
  })

  server.get(PROVET_PAY_PAYMENTS_ENDPOINTS.FILTERS, (schema, _request) => {
    const allPayments = schema.all('provetPayPaymentReport').models

    const uniqueValues = (key: keyof ProvetPayPaymentListItemAttributes) =>
      _uniq(allPayments.map((p) => p.attrs[key]))

    const filters: ProvetPayPaymentFilters = {
      account_description: uniqueValues('account_description'),
      channel: uniqueValues('channel'),
      clinic_location: uniqueValues('clinic_location'),
      paid_out: uniqueValues('paid_out'),
      payment_method: uniqueValues('payment_method'),
      payment_source: uniqueValues('payment_source'),
      payment_status: uniqueValues('payment_status'),
      result: uniqueValues('result'),
    }

    return {
      data: {
        id: 'filters',
        attributes: filters,
        type: PROVET_PAY_PAYMENTS_RESOURCE_TYPES.FILTERS,
      },
    }
  })

  // Groups endpoint for server-side grouping
  server.get(PROVET_PAY_PAYMENTS_ENDPOINTS.GROUPS, function (schema, request) {
    const groupByParam = request.queryParams?.group_by
    const groupBy = PROVET_PAY_PAYMENTS_GROUPABLE_COLUMNS.includes(
      groupByParam as (typeof PROVET_PAY_PAYMENTS_GROUPABLE_COLUMNS)[number],
    )
      ? (groupByParam as (typeof PROVET_PAY_PAYMENTS_GROUPABLE_COLUMNS)[number])
      : undefined

    if (!groupBy) {
      return {
        data: [],
        meta: { pagination: { page: 1, pages: 1, count: 0 } },
      }
    }

    const results = schema.all(MOCK_KEY)

    const filtered = filterable(results, request, {
      'account_description.in': (item, value) =>
        includesInCommaSeparated(item.attrs.account_description, value),
      'paid_out.in': (item, value) =>
        includesInCommaSeparated(item.attrs.paid_out, value),
      'payment_date_ts_utc.gte': (item, value) =>
        !isBefore(item.attrs.payment_date_ts_utc, value),
      'payment_date_ts_utc.lte': (item, value) =>
        !isAfter(item.attrs.payment_date_ts_utc, value),
      'payment_method.in': (item, value) =>
        includesInCommaSeparated(item.attrs.payment_method, value),
      'payment_status.in': (item, value) =>
        includesInCommaSeparated(item.attrs.payment_status, value),
      'payout_date_ts_utc.gte': (item, value) =>
        !!item.attrs.payout_date_ts_utc &&
        !isBefore(item.attrs.payout_date_ts_utc, value),
      'payout_date_ts_utc.lte': (item, value) =>
        !!item.attrs.payout_date_ts_utc &&
        !isAfter(item.attrs.payout_date_ts_utc, value),
      'result.in': (item, value) =>
        includesInCommaSeparated(item.attrs.result, value),
      search: (item, value) => {
        const searchLower = value.toLowerCase()
        return (item.attrs.related_invoice ?? '')
          .toLowerCase()
          .includes(searchLower)
      },
    })

    // Group items by the specified field
    const groupedData = filtered.models.reduce(
      (groups, item) => {
        const rawValue = item.attrs[groupBy]
        const groupValue =
          groupBy === 'payout_date_ts_utc' && typeof rawValue === 'string'
            ? rawValue.slice(0, 10)
            : String(rawValue || '')

        if (!groups[groupValue]) {
          groups[groupValue] = {
            groupValue: groupValue || null,
            items: [],
          }
        }
        groups[groupValue].items.push(item)
        return groups
      },
      {} as Record<
        string,
        {
          groupValue: string | null
          items: typeof filtered.models
        }
      >,
    )

    const sortParam = request.queryParams?.sort as string | undefined
    const sortDesc = sortParam?.startsWith('-') ?? false

    const groupsWithAggregations = Object.entries(groupedData).map(
      ([key, group]) => {
        const paymentCount = group.items.filter(
          (i) => i.attrs.payment_status === 'Payment',
        ).length
        const refundCount = group.items.filter(
          (i) => i.attrs.payment_status === 'Refund',
        ).length
        const disputeCount = group.items.filter((i) =>
          ['Dispute', 'Chargeback', 'ChargebackReversed'].includes(
            i.attrs.payment_status,
          ),
        ).length

        const totalPaymentAmount = group.items.reduce(
          (sum, i) => sum + toNumber(i.attrs.payment_amount),
          0,
        )
        const totalReversals = group.items.reduce(
          (sum, i) =>
            sum +
            toNumber(i.attrs.reversal_amount) +
            toNumber(i.attrs.reversal_fee),
          0,
        )
        const totalPaymentFees = group.items.reduce(
          (sum, i) =>
            sum +
            toNumber(i.attrs.nordhealth_fee) +
            toNumber(i.attrs.other_fees),
          0,
        )

        const attributes: ProvetPayPaymentGroupItemAttributes = {
          currency: MOCK_CURRENCY ?? '',
          dispute_count: disputeCount,
          group_by: groupBy,
          group_value: group.groupValue,
          net_amount: totalPaymentAmount - totalReversals - totalPaymentFees,
          payment_count: paymentCount,
          refund_count: refundCount,
          total_payment_amount: totalPaymentAmount,
          total_payment_fees: totalPaymentFees,
          total_reversals: totalReversals,
        }

        return {
          key,
          result: {
            id: `${groupBy}:${group.groupValue ?? key}`,
            type: PROVET_PAY_PAYMENTS_RESOURCE_TYPES.GROUP,
            attributes,
          },
        }
      },
    )

    const sortedGroups = groupsWithAggregations
      .sort((a, b) => {
        const cmp = String(a.key).localeCompare(String(b.key))
        return sortDesc ? -cmp : cmp
      })
      .map((g) => g.result)

    // Apply pagination to groups
    const pageNumberParam = request.queryParams?.['page[number]']
    const pageSizeParam = request.queryParams?.['page[size]']
    const pageNumber = parseInt(
      Array.isArray(pageNumberParam)
        ? pageNumberParam[0]
        : pageNumberParam || '1',
    )
    const pageSize = parseInt(
      Array.isArray(pageSizeParam) ? pageSizeParam[0] : pageSizeParam || '30',
    )
    const startIndex = (pageNumber - 1) * pageSize
    const endIndex = startIndex + pageSize
    const paginatedGroups = sortedGroups.slice(startIndex, endIndex)

    return {
      data: paginatedGroups,
      meta: {
        pagination: {
          page: pageNumber,
          pages: Math.ceil(sortedGroups.length / pageSize),
          count: sortedGroups.length,
        },
      },
    }
  })
}
